// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// The realtime layer. This runs ENTIRELY IN THE BROWSER — there is no scheduled Action behind
// it and no server anywhere. Two endpoints were verified to send `Access-Control-Allow-Origin: *`
// including on an OPTIONS preflight, so a static page can read them directly:
//
//   Queensland  airquality.des.qld.gov.au/v1/...
//   ACT         data.act.gov.au Socrata
//
// New South Wales CANNOT be live. Its API sends no access-control header at all and its OPTIONS
// preflight returns 404, so no browser can reach it. NSW stations therefore render from the last
// pipeline build with an explicit staleness chip. That asymmetry is stated on the map rather
// than hidden, because a reader in Sydney deserves to know why their city is not updating.
//
// Everything here is PROVISIONAL. Roughly one in five recent Queensland values is later revised
// once validated, so live readings are drawn in their own treatment and must never extend a line
// drawn from the validated archive.

import type { LiveReading, Station } from './types';

const QLD_MEASUREMENTS = 'https://airquality.des.qld.gov.au/v1/stations/parameters/measurements?pagesize=10000';
const QLD_STATIONS = 'https://airquality.des.qld.gov.au/v1/stations?pagesize=10000';
const ACT_ROWS =
  'https://www.data.act.gov.au/resource/94a5-zqnn.json?$order=datetime%20DESC&$limit=60' +
  '&$select=name,datetime,pm2_5_1_hr,pm10_1_hr';

/** parameter_id 31 is a reference PM2.5 instrument; 131 is a low-cost smoke sensor with the
 *  identical name, abbreviation, units and standard. Zero stations report both, so they cannot
 *  even be cross-calibrated — they are kept apart and labelled differently. */
const QLD_PM25_REFERENCE = 31;
const QLD_PM25_LOWCOST = 131;
const QLD_PM10 = 18;

/** -9999 is Queensland's sentinel and the API propagates it into its own index, so a missing
 *  reading arrives carrying `1hr_air_quality_index: -39996`. Reject it before anything else. */
function liveValue(raw: unknown): number | null {
  const value = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(value)) return null;
  if (value === -9999 || value === -1111 || value === -2222) return null;
  if (value < -50) return null;
  return value;
}

export interface LiveResult {
  readings: LiveReading[];
  fetchedAt: Date;
  newest: Date | null;
  errors: string[];
}

interface QldStation {
  station_id: string;
  station_name: string;
  latitude: number;
  longitude: number;
  station_type: string;
  end_date: string;
}

interface QldMeasurement {
  station_id: string;
  parameter_id: number;
  date_measured: string;
  mvalue: number;
  '1hr_air_quality_index'?: number;
}

async function fetchQld(signal: AbortSignal, byCluster: Map<string, string>): Promise<LiveReading[]> {
  const [stationsResponse, measurementsResponse] = await Promise.all([
    fetch(QLD_STATIONS, { signal }),
    fetch(QLD_MEASUREMENTS, { signal }),
  ]);
  if (!stationsResponse.ok || !measurementsResponse.ok) throw new Error('Queensland feed unavailable');
  const stations: QldStation[] = await stationsResponse.json();
  const measurements: QldMeasurement[] = await measurementsResponse.json();

  const stationById = new Map(stations.map((station) => [station.station_id, station]));
  // Keep only the newest hour per station and parameter. The feed returns a rolling window, so
  // without this the "current" reading would be whichever row happened to come last.
  const latest = new Map<string, QldMeasurement>();
  for (const row of measurements) {
    if (![QLD_PM25_REFERENCE, QLD_PM25_LOWCOST, QLD_PM10].includes(row.parameter_id)) continue;
    if (liveValue(row.mvalue) === null) continue;
    // The index is independently rejected when negative: a sentinel reading can carry a
    // plausible-looking value with an impossible index attached.
    if (typeof row['1hr_air_quality_index'] === 'number' && row['1hr_air_quality_index'] < 0) continue;
    const key = `${row.station_id}:${row.parameter_id}`;
    const existing = latest.get(key);
    if (!existing || row.date_measured > existing.date_measured) latest.set(key, row);
  }

  const readings = new Map<string, LiveReading>();
  for (const [key, row] of latest) {
    const station = stationById.get(row.station_id);
    if (!station || station.latitude === null || station.longitude === null) continue;
    const parameter = Number(key.split(':')[1]);
    let reading = readings.get(row.station_id);
    if (!reading) {
      reading = {
        clusterId: byCluster.get(`qld:${row.station_id}`) ?? null,
        stationName: station.station_name,
        jurisdiction: 'QLD',
        lat: station.latitude,
        lon: station.longitude,
        pm25: null,
        pm10: null,
        at: row.date_measured,
        lowCost: station.station_type === 'SMOKE',
      };
      readings.set(row.station_id, reading);
    }
    if (row.date_measured > reading.at) reading.at = row.date_measured;
    if (parameter === QLD_PM10) reading.pm10 = liveValue(row.mvalue);
    else reading.pm25 = liveValue(row.mvalue);
    if (parameter === QLD_PM25_LOWCOST) reading.lowCost = true;
  }
  return [...readings.values()].filter((reading) => reading.pm25 !== null || reading.pm10 !== null);
}

const ACT_COORDS: Record<string, [number, number]> = {
  civic: [-35.285307, 149.131579],
  monash: [-35.418, 149.086],
  florey: [-35.224, 149.046],
};

async function fetchAct(signal: AbortSignal, byCluster: Map<string, string>): Promise<LiveReading[]> {
  const response = await fetch(ACT_ROWS, { signal });
  if (!response.ok) throw new Error('ACT feed unavailable');
  const rows: Array<Record<string, string>> = await response.json();
  const seen = new Map<string, LiveReading>();
  for (const row of rows) {
    const name = String(row.name ?? '').toLowerCase();
    const coords = ACT_COORDS[name];
    if (!coords || seen.has(name)) continue;
    // Socrata omits null keys entirely rather than emitting null, so every field is read
    // defensively — the newest row genuinely lacks some of them.
    const pm25 = row.pm2_5_1_hr === undefined ? null : liveValue(row.pm2_5_1_hr);
    const pm10 = row.pm10_1_hr === undefined ? null : liveValue(row.pm10_1_hr);
    if (pm25 === null && pm10 === null) continue;
    seen.set(name, {
      clusterId: byCluster.get(`act:${row.name}`) ?? null,
      stationName: String(row.name),
      jurisdiction: 'ACT',
      lat: coords[0],
      lon: coords[1],
      pm25,
      pm10,
      at: row.datetime,
      lowCost: false,
    });
  }
  return [...seen.values()];
}

export async function fetchLive(stations: Station[], signal: AbortSignal): Promise<LiveResult> {
  const byCluster = new Map<string, string>();
  for (const station of stations) {
    for (const sourceId of station.sourceIds) byCluster.set(sourceId, station.id);
  }

  const errors: string[] = [];
  const settled = await Promise.allSettled([fetchQld(signal, byCluster), fetchAct(signal, byCluster)]);
  const readings: LiveReading[] = [];
  const labels = ['Queensland', 'ACT'];
  settled.forEach((result, index) => {
    if (result.status === 'fulfilled') readings.push(...result.value);
    else errors.push(`${labels[index]}: ${result.reason instanceof Error ? result.reason.message : 'unavailable'}`);
  });

  let newest: Date | null = null;
  for (const reading of readings) {
    const at = new Date(reading.at.length > 10 && reading.at.endsWith('Z') ? reading.at : `${reading.at.slice(0, 19)}Z`);
    if (!Number.isNaN(at.getTime()) && (!newest || at > newest)) newest = at;
  }

  return { readings, fetchedAt: new Date(), newest, errors };
}

export function relativeTime(from: Date, to = new Date()): string {
  const minutes = Math.round((to.getTime() - from.getTime()) / 60_000);
  if (!Number.isFinite(minutes)) return 'unknown';
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
